import { useEffect, useState } from 'react';

/**
 * Checks if any parent element has the specified class
 * @param {HTMLElement} element - The element to check parents for
 * @param {string} className - The class name to look for
 * @returns {boolean} - True if any parent has the class
 */
const hasParentWithClass = (element, className) => {
    if (!element || !element.parentElement) return false;
    
    let parent = element.parentElement;
    while (parent) {
        if (parent.classList && parent.classList.contains(className)) {
            return true;
        }
        parent = parent.parentElement;
    }
    return false;
};

/**
 * Detects if an element is inside a draggable section
 * @param {HTMLElement} element - The element to check
 * @returns {boolean} - True if the element is in a draggable section
 */
export const isInDraggableSection = (element) => {
    return hasParentWithClass(element, 'draggable-section') || 
           hasParentWithClass(element, 'section-content');
};

/**
 * Gets the appropriate class names based on environment
 * @param {HTMLElement} element - The element to check
 * @param {string} baseClass - The base class name
 * @returns {string} - The complete class string
 */
export const getEnvironmentClass = (element, baseClass) => {
    const classes = [baseClass];
    if (isInDraggableSection(element)) {
        classes.push('in-draggable-section');
    }
    return classes.join(' ');
};

/**
 * Gets the container class with theme and environment detection
 * @param {HTMLElement} element - The element to check
 * @param {string} theme - The current theme ('light' or 'dark')
 * @param {boolean} isMobileView - Whether the container is in mobile view
 * @returns {string} - The complete container class string
 */
export const getContainerClass = (element, theme, isMobileView) => {
    let classes = [`responsive-table-container`, `theme-${theme}`];
    
    if (isInDraggableSection(element)) {
        classes.push('in-draggable-section');
    }
    
    if (isMobileView) {
        classes.push('force-mobile-view');
    }
    
    return classes.join(' ');
};

/**
 * A React hook to detect if an element is in a draggable section
 * @param {React.RefObject} ref - The ref object attached to the element
 * @returns {boolean} - True if the element is in a draggable section
 */
export const useIsDraggable = (ref) => {
    const [isDraggable, setIsDraggable] = useState(false);

    useEffect(() => {
        if (ref.current) {
            setIsDraggable(isInDraggableSection(ref.current));
        }
    }, [ref]);

    return isDraggable;
}; 